/**
 * Bot Monitoring API Client
 */
import axios from 'axios'
import type {
  BotStatus,
  OffersResponse,
  LoansResponse,
  EarningsChartResponse,
} from '../../types/botMonitoring'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Get bot real-time status
 */
export async function getBotStatus(botId: number): Promise<BotStatus> {
  const response = await api.get<{ success: boolean; data?: BotStatus }>(`/bots/${botId}/status`)

  if (!response.data.success || !response.data.data) {
    throw new Error('Failed to fetch bot status')
  }

  return response.data.data
}

/**
 * Get bot offers list
 */
export async function getBotOffers(
  botId: number,
  params?: { page?: number; limit?: number }
): Promise<OffersResponse> {
  const response = await api.get<{ success: boolean; data?: OffersResponse }>(
    `/bots/${botId}/offers`,
    { params }
  )

  if (!response.data.success || !response.data.data) {
    throw new Error('Failed to fetch bot offers')
  }

  return response.data.data
}

/**
 * Get bot loans list
 */
export async function getBotLoans(
  botId: number,
  params?: { page?: number; limit?: number }
): Promise<LoansResponse> {
  const response = await api.get<{ success: boolean; data?: LoansResponse }>(
    `/bots/${botId}/loans`,
    { params }
  )

  if (!response.data.success || !response.data.data) {
    throw new Error('Failed to fetch bot loans')
  }

  return response.data.data
}

/**
 * Get bot earnings chart data
 */
export async function getEarningsChart(
  botId: number,
  days: number = 7
): Promise<EarningsChartResponse> {
  const response = await api.get<{ success: boolean; data?: import('../../types/botMonitoring').EarningsDataPoint[] }>(
    `/bots/${botId}/earnings-chart`,
    { params: { days } }
  )

  if (!response.data.success || !response.data.data) {
    throw new Error('Failed to fetch earnings chart')
  }

  return response.data.data
}
